import { Button, Grid, Stack, styled } from '@mui/material';
import CircularProgress from '@mui/material/CircularProgress';
import Typography from '@mui/material/Typography';
import React, { useRef } from 'react';
import { TFile } from 'types/user';
import { StyledParams } from 'types/common';
import axios from 'axios';
import { useAsyncCallback } from 'hooks/useAsyncCallback';
import { deleteFilePhotos, updateFilePhotoOnly } from 'services/admin';
import { IconDownLoad, IconNext } from 'assets/icon';
import { saveVideoFile } from 'utils/downloadFile';
import { InputSelectImage } from '../../../../../../components/elements/InputSelectImage';

type DocumentFileProps = {
  isUser: boolean;
  file: TFile;
  reloadPage: () => Promise<void>;
};
export const PhotoFile = ({ file, reloadPage, isUser }: DocumentFileProps) => {
  const fileInputDocumentRef = useRef<HTMLInputElement | null>(null);
  const [page, setPage] = React.useState(1);
  const [downloadingId, setDownloadingId] = React.useState<number | null>(null);
  const itemsPerPage = 5;
  const totalDocs = file?.photos?.length || 0;
  const totalPages = Math.ceil(totalDocs / itemsPerPage);
  const currentDocs = file?.photos?.slice((page - 1) * itemsPerPage, page * itemsPerPage);

  const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const fileList = event.target.files;
    if (!fileList) return;
    if (!window.confirm('Are you sure you want to add new photos?')) return;
    const newFiles = Array.from(fileList);
    const formData = new FormData();
    for (let x = 0; x < newFiles.length; x++) {
      formData.append('files', newFiles[x], newFiles[x].name);
    }
    const { data: resListIdFile } = await axios.post('/storages/file-photo', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
    await updateFilePhotoOnly(Number(file.id), resListIdFile.files);
    await reloadPage();
  };
  const handleDelete = async (id: number) => {
    if (!window.confirm('Are you sure you want to delete this photos?')) return;
    await deleteFilePhotos(id);
    await reloadPage();
  };

  const { asyncCallback } = useAsyncCallback(handleFileChange, []);
  const { asyncCallback: asyncHandleDelete } = useAsyncCallback(handleDelete, []);

  return (
    <Stack gap={'100%'}>
      <Stack flexDirection={'row'} alignItems={'center'} justifyContent={'space-between'}>
        <Typography fontSize={16} fontWeight={600} color={'#22374A'}>
          Photos ({file?.photos.length})
        </Typography>
        {!isUser && (
          <Button
            onClick={() => {
              if (fileInputDocumentRef.current) {
                fileInputDocumentRef.current?.click();
              }
            }}
            style={{
              padding: '0 10px',
              width: 'fit-content',
              background: '#22374A',
              borderRadius: '10px',
              border: '2px solid #22374A',
              color: '#fff',
              height: '32px',
              fontWeight: '600',
              fontSize: '12px',
              gap: '8px'
            }}>
            <img src={'/icons/icon_add_new.svg'} alt={'icon_delete'} width={'24px'} />
            New Photo
          </Button>
        )}
      </Stack>
      <Stack
        style={{
          minHeight: '258px',
          width: '100%',
          borderRadius: '10px',
          border: '1px solid #A9ABAE',
          marginTop: '14px',
          gap: 10
        }}>
        <Stack gap={1.25}>
          <Stack gap={1.25}>
            <Grid container justifyContent="flex-start" alignItems="center">
              {currentDocs?.map((doc, index) => (
                <Grid
                  item
                  xs={4}
                  sm={12}
                  md={12}
                  lg={12}
                  key={index}
                  style={{ position: 'relative' }}>
                  <Stack
                    px={'10px'}
                    py={'13px'}
                    borderBottom={index !== 4 ? '1px solid #A9ABAE' : 'none'}
                    flexDirection={'row'}
                    alignItems={'center'}
                    justifyContent={'space-between'}>
                    <Stack
                      width={'calc(100% - 62px)'}
                      flexDirection={'row'}
                      gap={1.5}
                      alignItems={'center'}>
                      <img src={doc.url} alt={'image'} width={'24px'} />
                      <NameDocument>{doc.name}</NameDocument>
                    </Stack>
                    <Stack flexDirection={'row'} alignItems={'center'} gap={'10px'}>
                      {downloadingId === doc.id ? (
                        <CircularProgress size={20} />
                      ) : (
                        <IconDownLoad
                          onClick={async () => {
                            try {
                              setDownloadingId(doc.id);
                              await saveVideoFile({ url: doc.url, fileName: doc.name });
                            } catch (e) {
                              console.log(e);
                            } finally {
                              setDownloadingId(null);
                            }
                          }}
                          style={{
                            cursor: 'pointer',
                            display: downloadingId === doc.id ? 'none' : 'block'
                          }}
                        />
                      )}
                      {!isUser && (
                        <img
                          onClick={() => asyncHandleDelete(doc.id)}
                          src={'/icons/icon_delete.svg'}
                          alt={'icon_delete'}
                          width={'24px'}
                          style={{ cursor: 'pointer' }}
                        />
                      )}
                    </Stack>
                  </Stack>
                </Grid>
              ))}
            </Grid>
          </Stack>
          <InputSelectImage
            listFileName={currentDocs?.map((item) => item.name)}
            ref={fileInputDocumentRef}
            onChange={asyncCallback}
          />
        </Stack>
      </Stack>
      <Stack
        marginTop={'12px'}
        flexDirection={'row'}
        alignItems={'center'}
        justifyContent={'space-between'}>
        <Typography fontSize={12} fontWeight={400} color={'#6D6E70'}>
          Showing {Math.min(page * itemsPerPage, totalDocs)} of {totalDocs} photos
        </Typography>
        <Stack flexDirection={'row'} alignItems={'center'} gap={'10px'}>
          <IconNext
            style={{
              cursor: page > 1 ? 'pointer' : 'not-allowed'
            }}
            onClick={() => page > 1 && setPage(page - 1)}
          />
          <IconNext
            style={{
              cursor: page < totalPages ? 'pointer' : 'not-allowed',
              transform: 'rotate(180deg)'
            }}
            onClick={() => page < totalPages && setPage(page + 1)}
          />
        </Stack>
      </Stack>
    </Stack>
  );
};
const NameDocument = styled(Typography)(({ theme }: StyledParams) => ({
  color: theme.colors.dark1,
  fontSize: 13,
  fontWeight: 500,
  lineHeight: '100%',
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  maxWidth: 'calc(100% - 60px)'
}));
